import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { 
  X, 
  Mail, 
  Lock, 
  User, 
  Loader2,
  ArrowLeft,
  CheckCircle,
  KeyRound
} from 'lucide-react';
import { FaGoogle } from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Animation variants extracted to prevent inline object re-renders
const fadeInVariants = {
  initial: { opacity: 0, x: 20 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -20 }
};

const modalOverlayVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 }
};

const modalContentVariants = {
  initial: { opacity: 0, scale: 0.95, y: 20 },
  animate: { opacity: 1, scale: 1, y: 0 },
  exit: { opacity: 0, scale: 0.95, y: 20 }
};

// Login Form Component
function LoginForm({ email, setEmail, password, setPassword, loading, onSubmit, onSwitchToSignup, onForgotPassword }) {
  return (
    <motion.div {...fadeInVariants}>
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="login-email">Email</Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              id="login-email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="pl-10"
              data-testid="login-email-input"
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="login-password">Password</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              id="login-password"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="pl-10"
              data-testid="login-password-input"
            />
          </div>
        </div>
        <div className="flex justify-end">
          <button type="button" onClick={onForgotPassword} className="text-sm text-primary hover:underline">
            Forgot password?
          </button>
        </div>
        <Button type="submit" disabled={loading} className="w-full rounded-full" data-testid="login-submit-btn">
          {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Signing in...</> : 'Sign in'}
        </Button>
        <p className="text-center text-sm text-muted-foreground pt-2">
          Don&apos;t have an account?{' '}
          <button onClick={onSwitchToSignup} className="text-primary hover:underline font-medium">Sign up</button>
        </p>
      </form>
    </motion.div>
  );
}

// Signup Form Component
function SignupForm({ email, setEmail, password, setPassword, confirmPassword, setConfirmPassword, name, setName, loading, onSubmit, onSwitchToLogin }) {
  return (
    <motion.div {...fadeInVariants}>
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="signup-name">Name</Label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input id="signup-name" type="text" placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} className="pl-10" data-testid="signup-name-input" />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="signup-email">Email</Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input id="signup-email" type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} className="pl-10" data-testid="signup-email-input" />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="signup-password">Password</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input id="signup-password" type="password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} className="pl-10" data-testid="signup-password-input" />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="signup-confirm">Confirm Password</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input id="signup-confirm" type="password" placeholder="••••••••" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="pl-10" data-testid="signup-confirm-input" />
          </div>
        </div>
        <Button type="submit" disabled={loading} className="w-full rounded-full" data-testid="signup-submit-btn">
          {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</> : 'Create account'}
        </Button>
        <p className="text-center text-sm text-muted-foreground">
          Already have an account?{' '}
          <button type="button" onClick={onSwitchToLogin} className="text-primary hover:underline font-medium">Sign in</button>
        </p>
      </form>
    </motion.div>
  );
}

// Forgot Password Form
function ForgotPasswordForm({ email, setEmail, loading, onSubmit, onBack }) {
  return (
    <motion.div {...fadeInVariants}>
      <form onSubmit={onSubmit} className="space-y-4">
        <p className="text-muted-foreground text-sm text-center mb-4">
          Enter your email and we&apos;ll send you a link to reset your password.
        </p>
        <div className="space-y-2">
          <Label htmlFor="forgot-email">Email</Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input id="forgot-email" type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} className="pl-10" data-testid="forgot-email-input" />
          </div>
        </div>
        <Button type="submit" disabled={loading} className="w-full rounded-full" data-testid="forgot-submit-btn">
          {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</> : 'Send reset link'}
        </Button>
        <button type="button" onClick={onBack} className="flex items-center justify-center w-full text-sm text-muted-foreground hover:text-foreground gap-1">
          <ArrowLeft className="w-4 h-4" />Back to sign in
        </button>
      </form>
    </motion.div>
  );
}

// Reset Password Form
function ResetPasswordForm({ password, setPassword, confirmPassword, setConfirmPassword, loading, onSubmit }) {
  return (
    <motion.div {...fadeInVariants}>
      <form onSubmit={onSubmit} className="space-y-4">
        <p className="text-muted-foreground text-sm text-center mb-4">Enter your new password below.</p>
        <div className="space-y-2">
          <Label htmlFor="reset-password">New Password</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input id="reset-password" type="password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} className="pl-10" data-testid="reset-password-input" />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="reset-confirm">Confirm Password</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input id="reset-confirm" type="password" placeholder="••••••••" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="pl-10" data-testid="reset-confirm-input" />
          </div>
        </div>
        <Button type="submit" disabled={loading} className="w-full rounded-full" data-testid="reset-submit-btn">
          {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Resetting...</> : 'Reset password'}
        </Button>
      </form>
    </motion.div>
  );
}

// Verify Email View
function VerifyEmailView({ email, loading, onResend }) {
  return (
    <motion.div {...fadeInVariants} className="text-center space-y-4">
      <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
        <Mail className="w-8 h-8 text-primary" />
      </div>
      <p className="text-muted-foreground">
        We&apos;ve sent a verification link to <strong className="text-foreground">{email}</strong>
      </p>
      <p className="text-sm text-muted-foreground">Click the link in the email to verify your account.</p>
      <Button variant="outline" onClick={onResend} disabled={loading} className="rounded-full" data-testid="resend-verification-btn">
        {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</> : 'Resend verification email'}
      </Button>
    </motion.div>
  );
}

// Success Views
function ForgotSentView({ onClose }) {
  return (
    <motion.div {...fadeInVariants} className="text-center space-y-4">
      <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
        <CheckCircle className="w-8 h-8 text-primary" />
      </div>
      <p className="text-muted-foreground">Check your inbox for a password reset link.</p>
      <Button onClick={onClose} className="rounded-full" data-testid="close-forgot-sent-btn">Done</Button>
    </motion.div>
  );
}

function ResetSuccessView({ onSwitchToLogin }) {
  return (
    <motion.div {...fadeInVariants} className="text-center space-y-4">
      <div className="w-16 h-16 mx-auto rounded-full bg-green-100 dark:bg-green-900/20 flex items-center justify-center">
        <KeyRound className="w-8 h-8 text-green-600" />
      </div>
      <p className="text-muted-foreground">Your password has been reset successfully!</p>
      <Button onClick={onSwitchToLogin} className="rounded-full" data-testid="login-after-reset-btn">Sign in with new password</Button>
    </motion.div>
  );
}

// Options View
function OptionsView({ onGoogleLogin, onEmailLogin, onSignup }) {
  return (
    <div className="space-y-4">
      <Button onClick={onGoogleLogin} className="w-full h-12 gap-3 rounded-xl text-base" variant="outline" data-testid="auth-google-btn">
        <FaGoogle className="w-5 h-5" />Continue with Google
      </Button>
      <div className="relative">
        <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
        <div className="relative flex justify-center text-xs uppercase"><span className="bg-card px-2 text-muted-foreground">or</span></div>
      </div>
      <Button onClick={onEmailLogin} className="w-full h-12 gap-3 rounded-xl text-base" data-testid="auth-email-btn">
        <Mail className="w-5 h-5" />Continue with Email
      </Button>
      <p className="text-center text-sm text-muted-foreground pt-2">
        Don&apos;t have an account?{' '}
        <button onClick={onSignup} className="text-primary hover:underline font-medium">Sign up</button>
      </p>
    </div>
  );
}

// Main AuthModal Component
export function AuthModal({ isOpen, onClose, onGoogleLogin, onLoginSuccess, initialMode = 'options', resetToken = null }) {
  const [mode, setMode] = useState(resetToken ? 'reset' : initialMode);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');

  useEffect(() => {
    if (resetToken && isOpen) setMode('reset');
  }, [resetToken, isOpen]);

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setName('');
    setMode(resetToken ? 'reset' : 'options');
  };

  const handleClose = () => { resetForm(); onClose(); };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!email || !password || !name) { toast.error('Please fill in all fields'); return; }
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (password !== confirmPassword) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/signup`, { email, password, name });
      setMode('verify');
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Signup failed');
    } finally { setLoading(false); }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error('Please fill in all fields'); return; }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      toast.success('Login successful!');
      onLoginSuccess(response.data);
      handleClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
      if (error.response?.status === 403) setMode('verify');
    } finally { setLoading(false); }
  };

  const handleResendVerification = async () => {
    if (!email) { toast.error('Please enter your email'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, { email });
      toast.success('Verification email sent!');
    } catch (error) { toast.error('Failed to send verification email'); }
    finally { setLoading(false); }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) { toast.error('Please enter your email'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setMode('forgot-sent');
      toast.success('Password reset email sent!');
    } catch (error) { toast.error('Failed to send reset email'); }
    finally { setLoading(false); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!password || !confirmPassword) { toast.error('Please fill in all fields'); return; }
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (password !== confirmPassword) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token: resetToken, new_password: password });
      setMode('reset-success');
      toast.success('Password reset successful!');
    } catch (error) { toast.error(error.response?.data?.detail || 'Reset failed'); }
    finally { setLoading(false); }
  };

  if (!isOpen) return null;

  const titles = {
    options: 'Welcome to Alertafy',
    login: 'Sign in',
    signup: 'Create account',
    verify: 'Verify your email',
    forgot: 'Forgot password',
    'forgot-sent': 'Check your email',
    reset: 'Reset password',
    'reset-success': 'Password reset!'
  };

  const canGoBack = !['options', 'verify', 'forgot-sent', 'reset', 'reset-success'].includes(mode);

  return (
    <AnimatePresence>
      <motion.div {...modalOverlayVariants} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={handleClose}>
        <motion.div {...modalContentVariants} className="w-full max-w-md bg-card border border-border rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
          <div className="relative px-6 pt-6 pb-4 border-b border-border/50">
            {canGoBack && (
              <button onClick={() => setMode('options')} className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full hover:bg-muted transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <button onClick={handleClose} className="absolute right-4 top-1/2 -translate-y-1/2 p-2 rounded-full hover:bg-muted transition-colors">
              <X className="w-5 h-5" />
            </button>
            <h2 className="text-xl font-bold text-center">{titles[mode]}</h2>
          </div>
          <div className="p-6">
            {mode === 'options' && <OptionsView onGoogleLogin={onGoogleLogin} onEmailLogin={() => setMode('login')} onSignup={() => setMode('signup')} />}
            {mode === 'login' && <LoginForm email={email} setEmail={setEmail} password={password} setPassword={setPassword} loading={loading} onSubmit={handleLogin} onSwitchToSignup={() => setMode('signup')} onForgotPassword={() => setMode('forgot')} />}
            {mode === 'signup' && <SignupForm email={email} setEmail={setEmail} password={password} setPassword={setPassword} confirmPassword={confirmPassword} setConfirmPassword={setConfirmPassword} name={name} setName={setName} loading={loading} onSubmit={handleSignup} onSwitchToLogin={() => setMode('login')} />}
            {mode === 'verify' && <VerifyEmailView email={email} loading={loading} onResend={handleResendVerification} />}
            {mode === 'forgot' && <ForgotPasswordForm email={email} setEmail={setEmail} loading={loading} onSubmit={handleForgotPassword} onBack={() => setMode('login')} />}
            {mode === 'forgot-sent' && <ForgotSentView onClose={handleClose} />}
            {mode === 'reset' && <ResetPasswordForm password={password} setPassword={setPassword} confirmPassword={confirmPassword} setConfirmPassword={setConfirmPassword} loading={loading} onSubmit={handleResetPassword} />}
            {mode === 'reset-success' && <ResetSuccessView onSwitchToLogin={() => setMode('login')} />}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
